/**
 * Holds the React mount until Convert has run, so variations are applied before the first render.
 * `window.__convertReady` is created by the inline snippet in the page's html-injections.ts and is
 * absent on pages without a convertProjectId.
 *
 * Resolves with whether Convert actually made it — re-checked at the moment we stop waiting, so it
 * reports the real state rather than which of the three exits fired.
 *
 * Three ways out, whichever comes first:
 *  - `snippet.experiences_evaluated` (or the snippet failing to load) resolves `__convertReady`;
 *  - the poll below sees `window.convert.currentData`, which the snippet builds while it runs. This
 *    covers the case where the event never fires at all — it only fires when there are active
 *    experiences to evaluate, so without the poll a page with no running experiment would hold the
 *    loader for the full timeout. A timer can only observe `currentData` after the snippet's own
 *    task has finished, so seeing it means evaluation is done;
 *  - the timeout, a hard ceiling: the convert-proxy can be slow (cold cache means a curl to
 *    upstream with a 5s timeout) or dead, and neither may leave the visitor on the loader.
 */
const MOUNT_HOLD_TIMEOUT_MS = 3000;
const POLL_INTERVAL_MS = 100;

type Exit = 'already-ready' | 'snippet' | 'poll' | 'timeout';

function isConvertReady(): boolean {
  return Boolean(window.convert?.currentData);
}

function log(state: string, waitedMs: number): void {
  console.log(`[Convert]: ${state} — waited ${waitedMs}ms`);
}

export default function convertReady(): Promise<boolean> {
  const ready = window.__convertReady;
  // No snippet on this page — nothing to wait for and nothing worth logging.
  if (!ready) return Promise.resolve(isConvertReady());

  const startedAt = Date.now();
  const waited = () => Date.now() - startedAt;

  if (isConvertReady()) {
    log('ready (already-ready)', waited());
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    let poll = 0;
    let deadline = 0;
    let settled = false;

    // Guarded: a later exit still fires (the event can arrive after the poll won) and must not
    // log twice.
    const done = (exit: Exit) => {
      if (settled) return;
      settled = true;
      clearInterval(poll);
      clearTimeout(deadline);
      const convertLoaded = isConvertReady();
      log(`${convertLoaded ? 'ready' : 'not ready'} (${exit})`, waited());
      resolve(convertLoaded);
    };

    poll = window.setInterval(() => {
      if (isConvertReady()) done('poll');
      else log('load check', waited());
    }, POLL_INTERVAL_MS);
    deadline = window.setTimeout(() => done('timeout'), MOUNT_HOLD_TIMEOUT_MS);

    ready.then(
      () => done('snippet'),
      () => done('snippet'),
    );
  });
}
